import { useEffect, useState } from "react";
import { ArrowRightLeft, FileDown, LogOut, Mail, RotateCcw } from "lucide-react";
import { api, apiError, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { usePermisos } from "@/lib/permisos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const MOTIVOS = ["Retiro voluntario", "Traslado a otro centro", "Egresado / graduado",
                 "Falta de pago", "Cambio de domicilio", "Otro"];

const CONDICIONES = [
  { v: "retirado", label: "Retirado del centro" },
  { v: "transferido", label: "Transferido a otro centro" },
  { v: "egresado", label: "Egresado / graduado" },
];

export const descargarConstancia = (est) =>
  descargarPdf(`/estudiantes/${est.id}/constancia-transferencia`, {},
               `Constancia-${est.nombre.replace(/ /g, "-")}.pdf`);

export const enviarConstancia = async (est) => {
  try {
    const { data } = await api.post(`/estudiantes/${est.id}/constancia/enviar`);
    toast.success(`${data.documento} enviada a ${data.enviado_a}`, {
      description: `El enlace permite ${data.descargas_max} descargas y caduca en 7 días.`,
    });
  } catch (e) {
    toast.error(apiError(e));
  }
};

/** Diálogo para retirar, transferir o egresar a un estudiante. */
export const DialogoSalida = ({ estudiante, onClose, onListo }) => {
  const [form, setForm] = useState({
    estado: "retirado", motivo: MOTIVOS[0], motivoLibre: "", fecha: "",
    centro_destino: "", observaciones: "", anular_pendientes: false,
  });
  const [cuenta, setCuenta] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!estudiante) return;
    setCuenta(null);
    api.get(`/estudiantes/${estudiante.id}/cuenta-resumen`)
      .then((r) => setCuenta(r.data)).catch(() => setCuenta(null));
  }, [estudiante]);

  const guardar = async () => {
    const motivo = form.motivo === "Otro" ? form.motivoLibre.trim() : form.motivo;
    if (!motivo) return toast.error("Escriba el motivo de la salida");
    setGuardando(true);
    try {
      const { data } = await api.post(`/estudiantes/${estudiante.id}/salida`, {
        estado: form.estado, motivo,
        fecha: form.fecha ? new Date(form.fecha).toISOString() : null,
        centro_destino: form.centro_destino || null,
        observaciones: form.observaciones || null,
        anular_pendientes: form.anular_pendientes,
      });
      toast.success(data.facturas_anuladas
        ? `${estudiante.nombre} quedó ${form.estado}; se anularon ${data.facturas_anuladas} factura(s)`
        : `${estudiante.nombre} quedó ${form.estado}`);
      onListo();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
    setGuardando(false);
  };

  return (
    <Dialog open={!!estudiante} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-salida">
        <DialogHeader>
          <DialogTitle className="font-display">Retirar o transferir a {estudiante?.nombre}</DialogTitle>
          <DialogDescription>
            Sale de los listados activos, de los reportes y de la facturación. Su historial de notas,
            asistencia y facturas se conserva completo y puede reactivarse cuando quiera.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Condición</Label>
            <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}
                    data-testid="salida-estado"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              {CONDICIONES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Motivo</Label>
              <select value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                      data-testid="salida-motivo"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Fecha de salida</Label>
              <Input type="date" value={form.fecha} data-testid="salida-fecha"
                     onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
          </div>

          {form.motivo === "Otro" && (
            <div className="space-y-2">
              <Label>Escriba el motivo</Label>
              <Input value={form.motivoLibre} data-testid="salida-motivo-libre"
                     onChange={(e) => setForm({ ...form, motivoLibre: e.target.value })} />
            </div>
          )}

          {form.estado === "transferido" && (
            <div className="space-y-2">
              <Label>Centro de destino</Label>
              <Input value={form.centro_destino} data-testid="salida-centro-destino"
                     placeholder="Nombre del colegio que lo recibe"
                     onChange={(e) => setForm({ ...form, centro_destino: e.target.value })} />
            </div>
          )}

          <div className="space-y-2">
            <Label>Observaciones</Label>
            <Input value={form.observaciones} data-testid="salida-observaciones"
                   onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>

          {cuenta && cuenta.pendientes > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4"
                 data-testid="salida-aviso-deuda">
              <p className="text-sm font-semibold text-destructive">
                Tiene {cuenta.pendientes} factura(s) pendiente(s) por {money(cuenta.balance)}
                {cuenta.vencidas > 0 ? ` (${cuenta.vencidas} vencida[s])` : ""}
              </p>
              <label className="mt-3 flex items-start gap-3 text-sm">
                <Checkbox checked={form.anular_pendientes} data-testid="salida-anular-pendientes"
                          onCheckedChange={(v) => setForm({ ...form, anular_pendientes: !!v })} />
                <span>
                  Anular esas facturas al retirarlo
                  <span className="block text-xs text-muted-foreground">
                    Si no la marca, las facturas siguen vivas y el estudiante continúa en morosidad.
                  </span>
                </span>
              </label>
            </div>
          )}

          <Button className="w-full rounded-full py-6" onClick={guardar} disabled={guardando}
                  data-testid="confirmar-salida">
            {guardando ? "Guardando…" : "Confirmar salida"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Diálogo para mover al estudiante de grado o sección dentro del mismo centro. */
export const DialogoTraslado = ({ estudiante, grados, onClose, onListo }) => {
  const [form, setForm] = useState({ grado: "", seccion: "", ajustar_materias: true });

  useEffect(() => {
    if (estudiante) setForm({ grado: estudiante.grado || "", seccion: estudiante.seccion || "", ajustar_materias: true });
  }, [estudiante]);

  const guardar = async () => {
    if (!form.grado.trim()) return toast.error("Indique el grado de destino");
    try {
      await api.post(`/estudiantes/${estudiante.id}/traslado-grado`, form);
      toast.success(`${estudiante.nombre} pasó a ${form.grado}${form.seccion ? ` ${form.seccion}` : ""}`);
      onListo();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={!!estudiante} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="modal-traslado">
        <DialogHeader>
          <DialogTitle className="font-display">Mover de grado a {estudiante?.nombre}</DialogTitle>
          <DialogDescription>
            Cambia el grado o la sección dentro del mismo colegio y avisa al tutor por el portal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Grado de destino</Label>
            <input list="grados-traslado" value={form.grado} data-testid="traslado-grado"
                   onChange={(e) => setForm({ ...form, grado: e.target.value })}
                   className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            <datalist id="grados-traslado">
              {grados.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label>Sección</Label>
            <Input value={form.seccion} data-testid="traslado-seccion"
                   onChange={(e) => setForm({ ...form, seccion: e.target.value })} />
          </div>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={form.ajustar_materias} data-testid="traslado-ajustar-materias"
                      onCheckedChange={(v) => setForm({ ...form, ajustar_materias: !!v })} />
            <span>
              Asignarle las materias del nuevo grado
              <span className="block text-xs text-muted-foreground">
                Reemplaza las materias actuales por las del grado de destino.
              </span>
            </span>
          </label>
          <Button className="w-full rounded-full py-6" onClick={guardar} data-testid="confirmar-traslado">
            <ArrowRightLeft className="mr-2 h-4 w-4" /> Mover de grado
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Listado de estudiantes retirados, transferidos o egresados. */
export const ListaSalidas = ({ estudiantes, onListo, onFicha }) => {
  const { puede } = usePermisos();
  const reactivar = async (e) => {
    try {
      await api.post(`/estudiantes/${e.id}/reactivar`);
      toast.success(`${e.nombre} volvió a estudiantes activos`);
      onListo();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (estudiantes.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground"
           data-testid="salidas-vacias">
        No hay estudiantes retirados ni transferidos.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-card" data-testid="lista-salidas">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Estudiante</th>
              <th className="px-6 py-3">Condición</th>
              <th className="px-6 py-3">Motivo</th>
              <th className="px-6 py-3">Salida</th>
              <th className="px-6 py-3">Centro destino</th>
              <th className="px-6 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {estudiantes.map((e) => (
              <tr key={e.id} className="border-t border-border/60 hover:bg-muted/20"
                  data-testid={`salida-fila-${e.matricula}`}>
                <td className="px-6 py-4">
                  <button className="text-left" onClick={() => onFicha(e.id)}
                          data-testid={`salida-ficha-${e.matricula}`}>
                    <p className="font-medium">{e.nombre}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {e.matricula} · {e.grado}
                    </p>
                  </button>
                </td>
                <td className="px-6 py-4">
                  <span className={`rounded-full px-2.5 py-1 text-xs ${
                    e.estado === "transferido" ? "bg-primary/10 text-primary"
                      : e.estado === "egresado" ? "bg-accent/15 text-accent-foreground"
                      : "bg-muted text-muted-foreground"}`}>
                    {e.estado}
                  </span>
                </td>
                <td className="px-6 py-4 text-muted-foreground">{e.motivo_salida || "—"}</td>
                <td className="px-6 py-4 text-muted-foreground">{e.fecha_salida ? fecha(e.fecha_salida) : "—"}</td>
                <td className="px-6 py-4 text-muted-foreground">{e.centro_destino || "—"}</td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button size="sm" variant="outline" className="rounded-full"
                            onClick={() => descargarConstancia(e).catch(() => toast.error("No se pudo generar la constancia"))}
                            data-testid={`constancia-${e.matricula}`}>
                      <FileDown className="mr-1 h-3 w-3" /> Constancia
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full"
                            onClick={() => enviarConstancia(e)}
                            title="Enviar la constancia por correo a la familia"
                            data-testid={`enviar-constancia-${e.matricula}`}>
                      <Mail className="mr-1 h-3 w-3" /> Enviar
                    </Button>
                    {puede("estudiantes:eliminar") && (
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => reactivar(e)}
                              data-testid={`reactivar-${e.matricula}`}>
                        <RotateCcw className="mr-1 h-3 w-3" /> Reactivar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const IconoSalida = LogOut;
